// Dépôt d'une demande en étapes : éligibilité, formulaire (moteur partagé, brouillon enregistré
// automatiquement sur le serveur et sur l'appareil), pièces, relecture, dépôt et confirmation.
// Les erreurs sont expliquées sans perte de saisie ; un brouillon est repris par ?dossier=.
import { validerDonnees, type DonneesFormulaire } from '@cc/contrats';
import { Bandeau, Bouton, Formulaire, Squelette, useBrouillon } from '@cc/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router';
import { EtatRequete } from '../../../commun/EtatRequete';
import { useDossierUsager, useFicheDemarche, useMesDossiers, useRepresentations } from '../api';
import { codeCollectiviteValide } from '../collectivite';
import { EnTeteCitoyen, ExigeConnexion } from '../commun';
import { useSessionUsager } from '../session/FournisseurSessionUsager';
import { EtapeEligibilite } from './EtapeEligibilite';
import { Confirmation, EtapeRelecture, erreursDepot, type EtapeDepot, type ErreurDepot } from './EtapeRelecture';
import { donneesInitiales, erreursPourResume, FormulaireDynamique, sansErreursDe } from './FormulaireDynamique';
import { erreursFormulaireApi, tarifsDe, versFormulaire, type ErreurSaisie } from './formulaire-depuis-fiche';
import { SelecteurPieces } from './SelecteurPieces';
import { useDepot } from './useDepot';
import styles from './depot.module.css';
import commun from '../citoyen.module.css';

const ETAPES: { id: EtapeDepot; libelle: string }[] = [
  { id: 'eligibilite', libelle: 'Vérifier' },
  { id: 'formulaire', libelle: 'Remplir' },
  { id: 'pieces', libelle: 'Pièces' },
  { id: 'relecture', libelle: 'Relire' },
  { id: 'confirmation', libelle: 'Déposer' },
];

function FilEtapes({ courante }: { courante: EtapeDepot }) {
  const rang = ETAPES.findIndex((e) => e.id === courante);
  return (
    <ol className={styles.etapes} aria-label="Étapes du dépôt">
      {ETAPES.map((e, i) => (
        <li key={e.id} className={styles.etape} data-etat={i < rang ? 'faite' : i === rang ? 'courante' : 'a_venir'} aria-current={i === rang ? 'step' : undefined}>
          <span className={styles.etapeLibelle}>
            {i + 1}. {e.libelle}
          </span>
        </li>
      ))}
    </ol>
  );
}

/** Vrai si le champ (racine d'un chemin tel que « adresse.voie ») a la même valeur dans les deux jeux de données. */
function memeValeur(envoyees: DonneesFormulaire, courantes: DonneesFormulaire, champ: string): boolean {
  const racine = champ.split('.')[0] ?? champ;
  return JSON.stringify(envoyees[racine]) === JSON.stringify(courantes[racine]);
}

function Depot({ code, demarche }: { code: string; demarche: string }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const dossierId = params.get('dossier');
  const { profil } = useSessionUsager();
  const fiche = useFicheDemarche(code, demarche);
  const dossier = useDossierUsager(dossierId);
  const dossiers = useMesDossiers();
  const representations = useRepresentations();
  const depot = useDepot(dossierId);
  const formulaire = useMemo(() => (fiche.data ? versFormulaire(fiche.data) : null), [fiche.data]);
  const tarifs = useMemo(() => (fiche.data ? tarifsDe(fiche.data) : {}), [fiche.data]);
  const [etape, setEtape] = useState<EtapeDepot>(dossierId ? 'formulaire' : 'eligibilite');
  const [donnees, setDonnees] = useState<DonneesFormulaire>({});
  const [erreurs, setErreurs] = useState<ErreurSaisie[]>([]);
  const [erreursDepotListe, setErreursDepotListe] = useState<ErreurDepot[]>([]);
  const initialise = useRef<string | null>(null);
  const derniereSauvegarde = useRef<string>('');
  const [enregistreLe, setEnregistreLe] = useState<Date | null>(null);
  const [erreurEnregistrement, setErreurEnregistrement] = useState<string | null>(null);
  // Dernières données saisies, lues quand la réponse d'un enregistrement arrive après d'autres saisies.
  const donneesCourantes = useRef<DonneesFormulaire>(donnees);
  donneesCourantes.current = donnees;

  // Données initiales : valeurs par défaut du schéma, puis les données du brouillon.
  useEffect(() => {
    if (!formulaire || !dossier.data || initialise.current === dossier.data.id) return;
    initialise.current = dossier.data.id;
    const d = donneesInitiales(formulaire, dossier.data.donnees_formulaire);
    setDonnees(d);
    derniereSauvegarde.current = JSON.stringify(d);
    if (dossier.data.statut.code !== 'brouillon') setEtape('confirmation');
  }, [formulaire, dossier.data]);

  // Brouillon de l'appareil (H9-03) : filet si la connexion ou la session lâche avant l'enregistrement serveur.
  const brouillon = useBrouillon(dossierId ? `citoyen-depot:${dossierId}` : null, donnees, { actif: etape === 'formulaire' });

  // Enregistrement automatique sur le serveur, 1,5 s après la dernière saisie, tant que le dossier est un brouillon.
  const estBrouillon = dossier.data?.statut.code === 'brouillon';
  const { enregistrer } = depot;
  useEffect(() => {
    if (!dossierId || !estBrouillon || etape !== 'formulaire') return;
    const serialise = JSON.stringify(donnees);
    if (serialise === derniereSauvegarde.current) return;
    // Sur un réseau lent, la réponse peut arriver après d'autres saisies : un champ modifié depuis l'envoi
    // garde l'état de la saisie courante, ni l'erreur ni la validation d'une valeur dépassée ne s'y appliquent.
    const inchange = (champ: string) => {
      const code = champ.split('.')[0] ?? champ;
      return JSON.stringify(donnees[code]) === JSON.stringify(donneesCourantes.current[code]);
    };
    const minuterie = window.setTimeout(() => {
      enregistrer(donnees)
        .then(() => {
          derniereSauvegarde.current = serialise;
          setEnregistreLe(new Date());
          setErreurEnregistrement(null);
          // Les données acceptées n'ont plus d'erreur : celles des champs inchangés depuis l'envoi sont retirées.
          setErreurs((actuelles) => actuelles.filter((er) => !inchange(er.champ)));
        })
        .catch((e: unknown) => {
          // Le même contenu n'est pas renvoyé tant qu'il n'a pas changé ; l'erreur est expliquée.
          derniereSauvegarde.current = serialise;
          const parChamp = erreursFormulaireApi(e);
          if (parChamp.length === 0) {
            setErreurEnregistrement(e instanceof Error ? e.message : 'Enregistrement impossible');
            return;
          }
          // Une réponse tardive (poste ou serveur lent) ne contredit pas une correction déjà faite : seules les
          // erreurs des champs inchangés depuis l'envoi sont retenues, et elles s'ajoutent aux erreurs des autres champs.
          const retenues = parChamp.filter((x) => memeValeur(donnees, donneesCourantes.current, x.champ));
          if (retenues.length > 0) setErreurs((actuelles) => [...actuelles.filter((a) => !retenues.some((r) => r.champ === a.champ)), ...retenues]);
        });
    }, 1500);
    return () => window.clearTimeout(minuterie);
  }, [donnees, dossierId, estBrouillon, etape, enregistrer]);

  const commencer = async (representationId: string | null) => {
    if (!formulaire) return;
    try {
      const d = await depot.creer(code, demarche, representationId, {});
      setParams({ dossier: d.id }, { replace: true });
      setEtape('formulaire');
    } catch {
      // L'erreur est affichée par l'étape.
    }
  };

  const continuerFormulaire = async () => {
    if (!formulaire) return;
    const r = validerDonnees(formulaire, donnees, { tarifs });
    // Les champs fichier se renseignent après l'étape des pièces : ils ne bloquent pas ici.
    const bloquantes = r.erreurs.filter((e) => !(e.message.endsWith('champ obligatoire') && formulaire.sections.some((s) => s.champs.some((c) => c.code === e.champ && c.type === 'fichier'))));
    setErreurs(bloquantes);
    if (bloquantes.length > 0) return;
    try {
      await depot.enregistrer(donnees);
      derniereSauvegarde.current = JSON.stringify(donnees);
      setEtape('pieces');
    } catch (e) {
      const parChamp = erreursFormulaireApi(e);
      if (parChamp.length > 0) setErreurs(parChamp);
    }
  };

  const deposer = async () => {
    try {
      await depot.deposer();
      brouillon.effacer();
      setErreursDepotListe([]);
      setEtape('confirmation');
    } catch {
      setErreursDepotListe(erreursDepot(depot.detailsErreur, depot.codeErreur, depot.messageErreur));
    }
  };
  // Les erreurs du dépôt refusé arrivent après le rendu de l'exception : relecture à chaque changement.
  useEffect(() => {
    if (etape === 'relecture' && depot.erreur) setErreursDepotListe(erreursDepot(depot.detailsErreur, depot.codeErreur, depot.messageErreur));
  }, [depot.erreur, depot.detailsErreur, depot.codeErreur, depot.messageErreur, etape]);

  const chargement = fiche.isPending || (dossierId !== null && dossier.isPending) || (etape === 'eligibilite' && (dossiers.isPending || representations.isPending));
  const erreurChargement = fiche.error ?? dossier.error;
  const brouillons = (dossiers.data ?? []).filter((d) => d.statut.code === 'brouillon' && d.demarche.code === demarche && d.collectivite.code === code);
  const piecesDossier = (dossier.data?.pieces ?? []).map((p) => ({ id: p.document_id, titre: p.titre, type: p.type }));

  return (
    <>
      <EnTeteCitoyen
        titre={fiche.data?.identite.libelle ?? 'Nouvelle demande'}
        surTitre={fiche.data ? `${fiche.data.collectivite.nom} · nouvelle demande` : undefined}
        retour={etape === 'confirmation' ? undefined : `/citoyen/${code}/demarches/${demarche}`}
        libelleRetour="Fiche de la démarche"
      />
      <FilEtapes courante={etape} />
      <EtatRequete chargement={chargement} erreur={erreurChargement} onReessayer={() => void fiche.refetch()} fonction="La démarche" lignes={6}>
        {fiche.data && formulaire && profil ? (
          etape === 'eligibilite' ? (
            <EtapeEligibilite fiche={fiche.data} profil={profil} representations={representations.data ?? []} brouillons={brouillons} onCommencer={(r) => void commencer(r)} enCours={depot.enCours === 'creer'} erreur={depot.enCours === null ? depot.messageErreur : null} />
          ) : !dossier.data ? (
            <Squelette lignes={4} />
          ) : etape === 'formulaire' ? (
            <Formulaire
              libelle="Formulaire de la demande"
              onSoumettre={() => void continuerFormulaire()}
              erreurs={erreursPourResume(erreurs, 'depot')}
              brouillonEnregistreLe={enregistreLe}
              restauration={
                brouillon.brouillonDisponible && JSON.stringify(brouillon.brouillonDisponible) !== JSON.stringify(donnees)
                  ? { date: brouillon.brouillonDate, onReprendre: () => setDonnees(brouillon.brouillonDisponible as DonneesFormulaire), onIgnorer: brouillon.ignorer }
                  : null
              }
              actions={
                <>
                  <Bouton type="submit" chargement={depot.enCours === 'enregistrer' && erreurs.length === 0} libelleChargement="Enregistrement en cours">
                    Continuer vers les pièces
                  </Bouton>
                  <Bouton variante="discret" onClick={() => navigate(`/citoyen/mes-dossiers/${dossier.data?.id}`)}>
                    Reprendre plus tard
                  </Bouton>
                </>
              }
            >
              {erreurEnregistrement ? <Bandeau type="attention">Enregistrement du brouillon impossible pour l&apos;instant : {erreurEnregistrement}. Vos saisies restent sur cet appareil.</Bandeau> : null}
              <FormulaireDynamique
                formulaire={formulaire}
                donnees={donnees}
                onChange={setDonnees}
                onChampModifie={(code) => setErreurs((e) => sansErreursDe(e, code))}
                erreurs={erreurs}
                tarifs={tarifs}
                pieces={piecesDossier}
                prefixe="depot"
              />
            </Formulaire>
          ) : etape === 'pieces' ? (
            <div className={commun.pile}>
              <p className={commun.aide}>Les pièces attendues dépendent de vos réponses. Prenez-les en photo ou choisissez un fichier ; celles de votre coffre sont proposées.</p>
              <SelecteurPieces dossier={dossier.data} onCoffre={(p) => depot.attacherCoffre([p])} onFichier={(f, type) => depot.televerser(f, type)} desactive={depot.enCours !== null} />
              <div className={styles.actions}>
                <Bouton variante="secondaire" onClick={() => setEtape('formulaire')}>
                  Retour au formulaire
                </Bouton>
                <Bouton onClick={() => setEtape('relecture')} disabled={dossier.data.pieces_attendues.some((p) => p.obligatoire && !p.fournie)}>
                  Relire la demande
                </Bouton>
              </div>
            </div>
          ) : etape === 'relecture' ? (
            <EtapeRelecture fiche={fiche.data} formulaire={formulaire} dossier={dossier.data} donnees={donnees} erreurs={erreursDepotListe} onAller={setEtape} onDeposer={() => void deposer()} enCours={depot.enCours === 'deposer'} />
          ) : (
            <Confirmation dossier={dossier.data} />
          )
        ) : null}
      </EtatRequete>
    </>
  );
}

export function PageDepot() {
  const { code: brut, demarche } = useParams();
  const code = codeCollectiviteValide(brut);
  if (!code || !demarche) return <Navigate to="/citoyen" replace />;
  return (
    <ExigeConnexion titre="Connectez-vous pour déposer une demande">
      <Depot code={code} demarche={demarche} />
    </ExigeConnexion>
  );
}
